// The home page's live panel: agent actions arriving at a broker and being marked against the
// Danger Scale: allowed, allowed and the owner told, held for a person, or refused. It is a
// simulation (the panel says so), built from the scale's own classed actions.
import { Check, HandPalm, Prohibit, Bell } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { useReducedMotion } from "../../ui/hooks";
import { CLASSED_ACTIONS, DANGER_SCALE } from "../../tools/danger";

interface Line {
  id: number;
  agent: string;
  tool: string;
  action: string;
  level: number;
  verdict: "allowed" | "told" | "held" | "approved" | "refused";
}

const AGENTS = ["triage-bot", "pr-agent", "support-draft", "access-review", "release-agent"];
// A fixed order through the classed actions that shows every verdict within a few lines.
const ORDER = [0, 12, 2, 15, 7, 21, 10, 4, 16, 13, 8, 22, 5, 18, 11, 1, 23, 14];

function lineAt(n: number): Line[] {
  const a = CLASSED_ACTIONS[ORDER[n % ORDER.length]!]!;
  const base = { agent: AGENTS[n % AGENTS.length]!, tool: a.tool, action: a.action, level: a.level };
  const verdict = a.level <= 2 ? "allowed" : a.level === 3 ? "told" : a.level === 4 ? "held" : "refused";
  const first: Line = { id: n * 2, ...base, verdict };
  // A held action is followed by its approval, as the owner taps Approve on their phone.
  return verdict === "held" ? [first, { id: n * 2 + 1, ...base, verdict: "approved" }] : [first];
}

const VERDICT = {
  allowed: { Icon: Check, text: "allowed", cls: "text-moss" },
  told: { Icon: Bell, text: "allowed · owner told", cls: "text-mustard" },
  held: { Icon: HandPalm, text: "held for a person", cls: "text-signal" },
  approved: { Icon: Check, text: "approved on iPhone", cls: "text-moss" },
  refused: { Icon: Prohibit, text: "refused · level 5", cls: "text-signal" },
} as const;

const VISIBLE = 6;

export function OversightLog() {
  const reduced = useReducedMotion();
  const [lines, setLines] = useState<Line[]>(() => [0, 1, 2, 3].flatMap(lineAt).slice(-VISIBLE));
  const [n, setN] = useState(4);

  useEffect(() => {
    if (reduced) return;
    const t = setTimeout(() => {
      setLines((ls) => [...ls, ...lineAt(n)].slice(-VISIBLE));
      setN(n + 1);
    }, 1900);
    return () => clearTimeout(t);
  }, [n, reduced]);

  return (
    <div className="card overflow-hidden shadow-[0_30px_60px_-30px_rgb(0_0_0/0.35)]" aria-label="A simulated broker log" role="img">
      <div className="flex items-center justify-between border-b border-line bg-paper2/60 px-4 py-2.5">
        <span className="font-mono text-[11px] uppercase tracking-wider text-ink3">broker · live (simulated)</span>
        <span className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-line" />
          <span className="h-2.5 w-2.5 rounded-full bg-line" />
          <span className="h-2.5 w-2.5 animate-blink rounded-full bg-signal" />
        </span>
      </div>
      <ul className="divide-y divide-line/70 font-mono text-[12px]">
        {lines.map((l) => {
          const v = VERDICT[l.verdict];
          const level = DANGER_SCALE[l.level]!;
          return (
            <li key={l.id} className="grid animate-rise grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 px-4 py-2.5 sm:grid-cols-[auto_1fr_auto]">
              <span className={clsx("inline-flex h-6 min-w-6 items-center justify-center rounded-md border px-1.5 text-[11px] font-semibold", level.tone)} title={`Level ${l.level}: ${level.name}`}>
                L{l.level}
              </span>
              <span className="truncate text-ink">
                <span className="text-ink3">{l.agent} →</span> {l.tool}.{l.action.replaceAll(" ", "_")}
              </span>
              <span className={clsx("col-start-2 inline-flex items-center gap-1 sm:col-start-3", v.cls)}>
                <v.Icon weight="bold" className="h-3.5 w-3.5" aria-hidden /> {v.text}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
